import { Form, Formik } from "formik";
// import React from "react";
import * as Tabs from "@radix-ui/react-tabs";
import ShareUrl from "../Share";
import FormMainContent from "../layouts/FormMainContent";
import RevisorsErklaering from "./RevisorsErklaering";
import { getTexts } from "./texts";
import AdvokatensErklaering from "./AdvokatensErklaering";
import {
  KlientkontoerklaeringFormValues,
  KlientkontoerklaeringMetadata,
  Formulartype,
} from "../../interfaces/klientkontoerklaering";
import { isComplete } from "./util/isComplete";
import { KLIENTKONTOERKLAERING_FORM_VALIDATION_SCHEMA } from "./util/validationSchema";
import { KKE_FORM_INITIAL_VALUES } from "../../constants/klientkontoerklaering/initialValues";
import { useMemo, useState } from "react";
import { KlientkontoErklaering, Signeringstype } from "../../interfaces/api";
import KKEFormSideBar from "./KKEFormSideBar";
import KKEBrugervejlening from "./KKEBrugervejledning";

export default function KlientkontoerklaeringForm({
  formularType,
  id,
  initialValues,
  metadata,
  kke,
}: {
  id: string;
  kke: KlientkontoErklaering;
  initialValues: KlientkontoerklaeringFormValues;
  metadata: KlientkontoerklaeringMetadata;
  formularType: Formulartype;
}) {
  const [signeringstype, setSigneringstype] = useState<Signeringstype>(
    "Klientkontoerklaering",
  );
  // Back-fill required fields that are absent on older saved forms, because
  // they were added to the schema later. This used to mutate the
  // `initialValues` prop in place during render; it now produces a copy, so
  // the caller's object is left untouched and the result is stable across
  // re-renders.
  const normalizedInitialValues = useMemo(() => {
    const defaults: Partial<KlientkontoerklaeringFormValues> = {};
    if (!initialValues.revisors_handling_12_table) {
      defaults.revisors_handling_12_table =
        KKE_FORM_INITIAL_VALUES.revisors_handling_12_table;
    }
    if (!initialValues.revisors_handling_13_question_alt_2_uddybning_table) {
      defaults.revisors_handling_13_question_alt_2_uddybning_table =
        KKE_FORM_INITIAL_VALUES.revisors_handling_13_question_alt_2_uddybning_table;
    }
    if (!initialValues.revisors_handling_16_question_alt_2_uddybning_table) {
      defaults.revisors_handling_16_question_alt_2_uddybning_table =
        KKE_FORM_INITIAL_VALUES.revisors_handling_16_question_alt_2_uddybning_table;
    }
    if (!initialValues.advokatens_section_3_3) {
      defaults.advokatens_section_3_3 =
        KKE_FORM_INITIAL_VALUES.advokatens_section_3_3;
    }
    return { ...initialValues, ...defaults };
  }, [initialValues]);

  return (
    <Formik
      validateOnMount
      enableReinitialize
      initialValues={normalizedInitialValues}
      validationSchema={KLIENTKONTOERKLAERING_FORM_VALIDATION_SCHEMA}
      onSubmit={() => {}}
    >
      {(props) => {
        const { values, errors, touched, setTouched } = props;

        const texts = getTexts(values, metadata);
        const isCompleteHelpers = isComplete(
          texts,
          values,
          errors,
          touched,
          setTouched,
        );
        const { advokatens_complete, revisor_complete } = isCompleteHelpers;
        return (
          <>
            <h1 className="pb-3 display-1">Formular I - Tilsvar</h1>
            <KKEBrugervejlening />
            <Form className="card">
              <Tabs.Root
                defaultValue={"Klientkontoerklaering" as Signeringstype}
                onValueChange={(type) =>
                  setSigneringstype(type as Signeringstype)
                }
              >
                <div className="bg-white row g-0">
                  <div className="col-9 card-header pb-0 border-0">
                    <h2 className="display-2">{texts.formTitle}</h2>
                  </div>
                  <div className="col-3 px-4 pt-6">
                    <ShareUrl
                      heading="Invitér revisor til at udfylde eller godkende erklæringen"
                      text="Kopiér linket og send det til revisor eller andre, for at give dem
              adgang til at redigere denne erklæring."
                      loginToken={kke.loginToken}
                      type="KKE"
                    />
                  </div>
                </div>
                <Tabs.List className="c-tabs c-tabs--sticky" aria-label="Vælg">
                  <Tabs.Trigger
                    className="c-tabs-tab"
                    value={"Klientkontoerklaering" as Signeringstype}
                  >
                    {metadata.person !== undefined
                      ? "Advokatens"
                      : metadata.firma !== undefined
                        ? "Advokatselskabets"
                        : ""}{" "}
                    erklæring
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    className="c-tabs-tab"
                    value={"KlientkontoerklaeringRevisor" as Signeringstype}
                  >
                    Revisors erklæring
                  </Tabs.Trigger>
                </Tabs.List>
                <div className="row g-0">
                  <FormMainContent>
                    <Tabs.Content
                      value={"Klientkontoerklaering" as Signeringstype}
                      className={`${advokatens_complete ? "pb-8" : ""}`}
                    >
                      <AdvokatensErklaering
                        formikProps={props}
                        texts={texts}
                        isCompleteHelpers={isCompleteHelpers}
                        locked={
                          kke.signeret ||
                          kke.interessentSigneret ||
                          kke.revisorSigneret
                        }
                      />
                    </Tabs.Content>
                    <Tabs.Content
                      value={"KlientkontoerklaeringRevisor" as Signeringstype}
                    >
                      <RevisorsErklaering
                        formikProps={props}
                        texts={texts}
                        isCompleteHelpers={isCompleteHelpers}
                        locked={!advokatens_complete || kke.revisorSigneret}
                      />
                    </Tabs.Content>
                  </FormMainContent>
                  <KKEFormSideBar
                    advokatens_complete={advokatens_complete}
                    revisor_complete={revisor_complete}
                    formik={props}
                    kkeId={id}
                    metadata={metadata}
                    formularType={formularType}
                    kke={kke}
                  />
                </div>
                {signeringstype === "Klientkontoerklaering" &&
                advokatens_complete ? (
                  <Tabs.List className="c-tabs--bottom col-9 d-flex justify-content-center">
                    <Tabs.Trigger
                      className="c-tabs-tab link-primary"
                      value={"KlientkontoerklaeringRevisor" as Signeringstype}
                    >
                      Gå til Revisors erklæring
                    </Tabs.Trigger>
                  </Tabs.List>
                ) : (
                  ""
                )}
              </Tabs.Root>
            </Form>
          </>
        );
      }}
    </Formik>
  );
}
